import { useEffect, useRef, useState } from "react";
import { Header } from "../components/Header";
import { useDemoSettings } from "../contexts/demo-settings-context";
import { useAnalytics } from "../lib/analytics";
import { useClientTranslations } from "../lib/i18n/client";
import { callDigitalNurse } from "../services/digitalNurseService";
import styles from "./Communication.module.css";

interface ChatMessage {
	id: string;
	senderName: string;
	title: string;
	content: string;
	timestamp: string;
	isRead: boolean;
}

const MOCK_MESSAGES: ChatMessage[] = [
	{
		id: "mock-1",
		senderName: "Fertility Clinic of NY",
		title: "Appointment Confirmation",
		content:
			"Your appointment has been confirmed for March 4th at 2:00 PM. Please arrive early to complete any necessary paperwork.",
		timestamp: "2h ago",
		isRead: false,
	},
	{
		id: "mock-2",
		senderName: "A.D diagnostics Lab",
		title: "Lab Results Available",
		content:
			"Your recent lab results are now available in your patient portal. Please review them.",
		timestamp: "5h ago",
		isRead: true,
	},
	{
		id: "mock-3",
		senderName: "Fertility Clinic of NY",
		title: "Pre-Appointment Questionnaire",
		content:
			"Please complete the attached questionnaire before your upcoming appointment. This will help us better understand your medical history and concerns.",
		timestamp: "5h ago",
		isRead: true,
	},
];

export function Communication() {
	const { logEvent } = useAnalytics();
	const { t } = useClientTranslations("communication");

	const [showNurseModal, setShowNurseModal] = useState(false);
	const [nurseFormData, setNurseFormData] = useState({
		name: "",
		phone: "",
	});
	const [isSubmittingNurse, setIsSubmittingNurse] = useState(false);
	const { organizationName } = useDemoSettings();

	const hasTrackedPageView = useRef(false);
	useEffect(() => {
		if (!hasTrackedPageView.current) {
			hasTrackedPageView.current = true;
			logEvent("communication_page_viewed");
		}
	}, [logEvent]);

	const handleNurseRequest = async () => {
		if (!nurseFormData.name || !nurseFormData.phone) {
			return;
		}

		setIsSubmittingNurse(true);
		try {
			const data = await callDigitalNurse({
				fullName: nurseFormData.name,
				phoneNumber: nurseFormData.phone,
				...(organizationName ? { clinicName: organizationName } : {}),
			});

			if (data.success) {
				logEvent("digital_nurse_call_requested");
				setShowNurseModal(false);
				setNurseFormData({ name: "", phone: "" });
			}
		} catch (error) {
			console.error("Digital nurse call error:", error);
		} finally {
			setIsSubmittingNurse(false);
		}
	};

	const handleCloseModal = () => {
		setShowNurseModal(false);
		setNurseFormData({ name: "", phone: "" });
	};

	return (
		<div className={styles.wrapper}>
			<Header />
			<main className={styles.main}>
				<section className={styles.messagesSection}>
					<h2 className={styles.messagesHeading}>{t("messages.heading")}</h2>
					{MOCK_MESSAGES.length > 0 ? (
						<div className={styles.messagesList}>
							{MOCK_MESSAGES.map((message) => (
								<div
									key={message.id}
									className={`${styles.messageCard} ${
										message.isRead
											? styles.messageCardRead
											: styles.messageCardUnread
									}`}
								>
									<div className={styles.messageCardHeader}>
										<div className={styles.messageCardSenderRow}>
											<span
												className={`${styles.messageCardDot} ${
													message.isRead ? styles.dotRead : styles.dotUnread
												}`}
											/>
											<span
												className={
													message.isRead
														? styles.senderRead
														: styles.senderUnread
												}
											>
												{message.senderName}
											</span>
										</div>
										<span className={styles.messageCardTimestamp}>
											{message.timestamp}
										</span>
									</div>
									<p
										className={
											message.isRead ? styles.titleRead : styles.titleUnread
										}
									>
										{message.title}
									</p>
									<p className={styles.messageCardContent}>{message.content}</p>
								</div>
							))}
						</div>
					) : (
						<div className={styles.messagesEmptyState}>
							<img
								src="/images/bottom-black-nav-communication.svg"
								alt=""
								className={styles.emptyStateIcon}
							/>
							<h3 className={styles.emptyStateTitle}>
								{t("emptyState.title")}
							</h3>
							<p className={styles.emptyStateDescription}>
								{t("emptyState.description")}
							</p>
						</div>
					)}
				</section>
			</main>

			<div className={styles.nurseButtonContainer}>
				<button
					type="button"
					className={styles.callNurseButton}
					onClick={() => setShowNurseModal(true)}
				>
					{t("digitalNurse.callButton")}
				</button>
			</div>

			{showNurseModal && (
				<div
					role="dialog"
					aria-modal="true"
					className={styles.modalOverlay}
					onClick={handleCloseModal}
					onKeyDown={(e) => {
						if (e.key === "Escape") handleCloseModal();
					}}
				>
					<div
						role="document"
						className={styles.modalContent}
						onClick={(e) => e.stopPropagation()}
						onKeyDown={() => {}}
					>
						<div className={styles.modalHeader}>
							<h3 className={styles.modalTitle}>
								{t("digitalNurse.modalTitle")}
							</h3>
							<button
								type="button"
								className={styles.modalCloseButton}
								onClick={handleCloseModal}
							>
								<svg
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									aria-label="Close"
									role="img"
								>
									<line x1="18" y1="6" x2="6" y2="18" />
									<line x1="6" y1="6" x2="18" y2="18" />
								</svg>
							</button>
						</div>

						<div className={styles.modalBody}>
							<div className={styles.formField}>
								<label htmlFor="nurse-name" className={styles.formLabel}>
									{t("digitalNurse.yourName")}
								</label>
								<input
									id="nurse-name"
									type="text"
									className={styles.formInput}
									value={nurseFormData.name}
									onChange={(e) =>
										setNurseFormData({
											...nurseFormData,
											name: e.target.value,
										})
									}
									placeholder={t("digitalNurse.enterName")}
								/>
							</div>
							<div className={styles.formField}>
								<label htmlFor="nurse-phone" className={styles.formLabel}>
									{t("digitalNurse.phoneNumber")}
								</label>
								<input
									id="nurse-phone"
									type="tel"
									className={styles.formInput}
									value={nurseFormData.phone}
									onChange={(e) =>
										setNurseFormData({
											...nurseFormData,
											phone: e.target.value,
										})
									}
									placeholder={t("digitalNurse.enterPhone")}
								/>
							</div>
						</div>

						<div className={styles.modalFooter}>
							<button
								type="button"
								className={styles.submitButton}
								onClick={handleNurseRequest}
								disabled={
									isSubmittingNurse ||
									!nurseFormData.name ||
									!nurseFormData.phone
								}
							>
								{isSubmittingNurse
									? t("digitalNurse.submitting")
									: t("digitalNurse.requestCall")}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
